import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { lightTheme, darkTheme } from '../constants/themes';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'significa-ascii-theme';

/**
 * Get the user's system color scheme preference
 */
const getSystemPreference = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

/**
 * Get the initial theme from localStorage or system preference
 */
const getInitialTheme = () => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  }
  return getSystemPreference();
};

/**
 * ThemeProvider Component
 *
 * Provides theme context to the entire app.
 * Persists theme choice to localStorage.
 */
export const ThemeProvider = ({ children }) => {
  const [themeName, setThemeName] = useState(getInitialTheme);

  // Persist theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, themeName);
  }, [themeName]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only update if user hasn't manually set a preference
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        setThemeName(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setThemeName(prev => prev === 'light' ? 'dark' : 'light');
  };

  const theme = themeName === 'dark' ? darkTheme : lightTheme;

  const value = useMemo(() => ({
    themeName,
    theme,
    colors: theme.colors,
    toggleTheme,
    isDark: themeName === 'dark'
  }), [themeName, theme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * useTheme Hook
 *
 * Returns the current theme context including colors and toggle function.
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
